import { ActionCategory } from "../../generated/core/v1/actions";
import type { Date as PDate } from "../../generated/core/v1/date";
import { type Event, Event_Type, IncomeDetails_Source } from "../../generated/core/v1/events";
import { addMonths, compareDates, yearFraction } from "../lib/dates";
import { type FlatAction, actionsBetween, categoryTotals, flatActions, monthsIn } from "./actions";
import { type Plan, eventsBetween, eventsOf, startDate } from "./core";

/**
 * Income and cash flow from INCOME events and the plan's actions. Monthly figures are averages over
 * a window of the plan (default: its first twelve months), so one lumpy month doesn't skew them.
 */
export type IncomeSummary = {
  months: number;
  /** Before tax, a month. */
  gross: number;
  /** Income tax withheld, CPP, EI and pension contributions, a month. */
  deductions: number;
  /** What lands in the accounts, a month. */
  net: number;
  byMember: Map<string, { gross: number; net: number }>;
  bySource: Map<IncomeDetails_Source, { gross: number; net: number }>;
};

export function incomeEvents(plan: Plan, from?: PDate, to?: PDate): Event[] {
  return eventsBetween(plan, from, to, Event_Type.INCOME).filter(
    (e) => e.details.oneofKind === "income",
  );
}

/**
 * How many months of a window the plan covers. A plan that runs short stops on that day, so its
 * first year averaged over twelve months would understate every monthly figure.
 */
export function coveredMonths(plan: Plan, from: PDate, months: number): number {
  const short = eventsOf(plan, Event_Type.CASH_SHORTFALL)[0]?.date;
  if (!short || compareDates(short, addMonths(from, months)) >= 0) return months;
  return Math.max(1, (yearFraction(short) - yearFraction(from)) * 12);
}

export function incomeSummary(
  plan: Plan,
  window = 12,
  from = startDate(plan),
  /** income sources (by entity uuid) to leave out, e.g. a what-if's one-off payout */
  skip?: ReadonlySet<string>,
): IncomeSummary {
  const months = from ? coveredMonths(plan, from, window) : window;
  const empty: IncomeSummary = {
    months,
    gross: 0,
    deductions: 0,
    net: 0,
    byMember: new Map(),
    bySource: new Map(),
  };
  if (!from) return empty;
  const to = addMonths(from, window);
  const out = empty;
  incomeEvents(plan, from, to).forEach((e) => {
    if (e.details.oneofKind !== "income") return;
    if (skip?.has(e.entityUuid)) return;
    const d = e.details.income;
    out.gross += d.gross / months;
    out.deductions += d.deductions / months;
    out.net += d.net / months;
    const m = out.byMember.get(e.memberUuid) ?? { gross: 0, net: 0 };
    m.gross += d.gross / months;
    m.net += d.net / months;
    out.byMember.set(e.memberUuid, m);
    const s = out.bySource.get(d.source) ?? { gross: 0, net: 0 };
    s.gross += d.gross / months;
    s.net += d.net / months;
    out.bySource.set(d.source, s);
  });
  return out;
}

/** Grants (CESG, CLB) received in a window. */
export function grantTotal(plan: Plan, from?: PDate, to?: PDate): number {
  return eventsBetween(plan, from, to, Event_Type.GOVERNMENT_GRANT).reduce(
    (s, e) => s + (e.details.oneofKind === "income" ? e.details.income.net : 0),
    0,
  );
}

/** Where the money goes: the plan's outflows by category, a month, over a window. */
export type SpendSlice = { category: ActionCategory; monthly: number };

export function spendingByCategory(
  plan: Plan,
  months = 12,
  from = startDate(plan),
  all?: FlatAction[],
): SpendSlice[] {
  if (!from) return [];
  const to = addMonths(from, months);
  const totals = categoryTotals(actionsBetween(plan, from, to, all ?? flatActions(plan)));
  const covered = coveredMonths(plan, from, months);
  const n = covered < months ? covered : monthsIn(from, to);
  return [...totals.entries()]
    .map(([category, total]) => ({ category, monthly: total / n }))
    .filter((s) => s.monthly > 0.5)
    .sort((a, b) => ORDER.indexOf(a.category) - ORDER.indexOf(b.category));
}

/** The order slices are shown in (the prototype's essentials, lifestyle, debt, saved). */
export const ORDER: ActionCategory[] = [
  ActionCategory.ESSENTIAL,
  ActionCategory.HOUSING,
  ActionCategory.LIFESTYLE,
  ActionCategory.DEBT_MINIMUM,
  ActionCategory.DEBT_EXTRA,
  ActionCategory.PRIORITY,
  ActionCategory.SAVING,
  ActionCategory.OTHER,
  ActionCategory.UNSPECIFIED,
  ActionCategory.WITHDRAWAL,
];

/** Spending as the prototype groups it: essentials, lifestyle, debt, saved (a month). */
export type PaycheckSplit = {
  essentials: number;
  lifestyle: number;
  debt: number;
  saved: number;
  priorities: number;
  other: number;
};

export function paycheckSplit(slices: SpendSlice[]): PaycheckSplit {
  const get = (...cs: ActionCategory[]) =>
    slices.filter((s) => cs.includes(s.category)).reduce((a, s) => a + s.monthly, 0);
  return {
    essentials: get(ActionCategory.ESSENTIAL, ActionCategory.HOUSING),
    lifestyle: get(ActionCategory.LIFESTYLE),
    debt: get(ActionCategory.DEBT_MINIMUM, ActionCategory.DEBT_EXTRA),
    saved: get(ActionCategory.SAVING),
    priorities: get(ActionCategory.PRIORITY),
    other: get(ActionCategory.OTHER, ActionCategory.UNSPECIFIED),
  };
}

/** Money in against money out, one row per calendar month. */
export type MonthFlow = { month: PDate; moneyIn: number; moneyOut: number; saved: number };

export function monthlyFlows(plan: Plan, months = 12, from = startDate(plan)): MonthFlow[] {
  if (!from) return [];
  const all = flatActions(plan);
  const rows: MonthFlow[] = [];
  for (let i = 0; i < months; i++) {
    const a = i === 0 ? from : { ...addMonths(from, i), day: 1 };
    const b = { ...addMonths(from, i + 1), day: 1 };
    const acts = actionsBetween(plan, a, b, all);
    const moneyIn =
      incomeEvents(plan, a, b).reduce(
        (s, e) => s + (e.details.oneofKind === "income" ? e.details.income.net : 0),
        0,
      ) + grantTotal(plan, a, b);
    const outs = acts.filter((f) => f.direction === "out");
    const saved = outs
      .filter((f) => f.category === ActionCategory.SAVING)
      .reduce((s, f) => s + f.amount, 0);
    const moneyOut = outs
      .filter((f) => f.category !== ActionCategory.SAVING)
      .reduce((s, f) => s + f.amount, 0);
    rows.push({ month: a, moneyIn, moneyOut, saved });
  }
  return rows;
}

/** The bills: what goes out before the plan decides anything (debt minimums, not extra payments). */
const BILLS = new Set([
  ActionCategory.ESSENTIAL,
  ActionCategory.HOUSING,
  ActionCategory.LIFESTYLE,
  ActionCategory.DEBT_MINIMUM,
  ActionCategory.OTHER,
  ActionCategory.UNSPECIFIED,
]);

/**
 * What's left after the bills, a month, averaged over a window: take-home income less essentials,
 * housing, lifestyle, minimum debt payments, and other outflows. It is what the plan has for
 * priorities, extra debt payments, and savings (the prototype's "left over").
 */
export function leftOverMonthly(
  plan: Plan,
  months = 12,
  from = startDate(plan),
  skip?: ReadonlySet<string>,
): number {
  const bills = spendingByCategory(plan, months, from)
    .filter((s) => BILLS.has(s.category))
    .reduce((a, s) => a + s.monthly, 0);
  return incomeSummary(plan, months, from, skip).net - bills;
}

/**
 * What the plan pays toward each of the household's expenses, a month, averaged over a window of
 * its first months (Budget's default budget: the household's own spending, as the plan carries
 * it). Keyed by the expense's uuid (a mortgage's property tax by the mortgage's).
 */
export function spendingByExpense(
  plan: Plan,
  months = 12,
  from = startDate(plan),
): { uuid: string; category: ActionCategory; monthly: number }[] {
  if (!from) return [];
  const to = addMonths(from, months);
  const covered = coveredMonths(plan, from, months);
  const out = new Map<string, { category: ActionCategory; total: number }>();
  actionsBetween(plan, from, to)
    .filter((f) => f.kind === "moveMoneyFromChequingToExpenseAction")
    .forEach((f) => {
      const e = out.get(f.targetUuid) ?? { category: f.category, total: 0 };
      e.total += f.amount;
      out.set(f.targetUuid, e);
    });
  return [...out.entries()]
    .map(([uuid, e]) => ({ uuid, category: e.category, monthly: e.total / covered }))
    .filter((e) => e.monthly > 0.5)
    .sort((a, b) => b.monthly - a.monthly);
}

/**
 * Money kept a month: money in less money out (savings and investments count as kept, as on "Money
 * in versus money out"), averaged over a window of the plan's first months; and its total.
 */
export function keptOver(
  plan: Plan,
  months = 12,
): { monthly: number; total: number; moneyIn: number; moneyOut: number; months: number } {
  const from = startDate(plan);
  // A plan that runs short stops there: average over the months it covers.
  const n = from ? Math.min(months, Math.ceil(coveredMonths(plan, from, months))) : 0;
  const rows = monthlyFlows(plan, n);
  const moneyIn = rows.reduce((s, r) => s + r.moneyIn, 0);
  const moneyOut = rows.reduce((s, r) => s + r.moneyOut, 0);
  const total = moneyIn - moneyOut;
  return {
    monthly: rows.length ? total / rows.length : 0,
    total,
    moneyIn,
    moneyOut,
    months: rows.length,
  };
}
